<template>
  <div>
    <vue-html2pdf
      ref="html2Pdf"
      class="my-export"
      :show-layout="false"
      :preview-modal="false"
      :enable-download="false"
      :manual-pagination="false"
      :paginate-elements-by-height="1400"
      :filename="dynamicFilename"
      pdf-format="a4"
      pdf-content-width="auto"
      :html-to-pdf-options="htmlToPdfOptions"
      @progress="assignLodingTime($event)"
      @beforeDownload="onBeforeDownload"
      @hasDownloaded="hasDownloaded"
    >
      <section
        id="my-pdf"
        ref="pdfContent"
        slot="pdf-content"
        :style="pdfContentStyle"
      ></section>
    </vue-html2pdf>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { eventBus } from "@/main";
import { mapActions } from "vuex";
import VueHtml2pdf from "vue-html2pdf";
import Loading from "@/components/Loading";

export default {
  name: "pdfExport",
  components: {
    VueHtml2pdf,
    Loading,
  },
  data() {
    return {
      isLoading: false,
      isUpload: false,
      patientId: null,
      remark: "",
      dynamicFilename: "doc",
      userInteractionContext: null, // For iPad compatibility

      // Enhanced PDF options
      htmlToPdfOptions: {
        pagebreak: {
          mode: [],
          before: ".page-break-before",
          avoid: ".avoid-break",
        },
        image: {
          type: "jpeg",
          quality: 0.98,
        },
        margin: [15, 5, 15, 5], // Adjusted margins for better layout
        html2canvas: {
          dpi: 300,
          scale: 4,
          letterRendering: true,
          useCORS: true,
          logging: true, // Helpful for debugging
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
      },
      loadingTime: 0,
      pdfContentStyle: {
        letterSpacing: "0.01rem",
        fontSize: "13px",
        margin: "0.5rem 3rem",
        lineHeight: "1.5",
      },
    };
  },

  methods: {
    ...mapActions({
      createMediaFiles: "moduleMediaFile/createMediaFiles",
    }),

    async generatePdf({ header, body, footer, option }) {
      try {
        this.isLoading = true;
        this.isUpload = option?.isUpload || false;
        this.patientId = option?.patientId || null;
        this.remarkTitle = option?.remarkTitle || "";
        this.dynamicFilename = option?.filename || "doc";
        this.htmlToPdfOptions.jsPDF.orientation =
          option?.orientation || "portrait";

        // Apply custom styles for better page breaks
        if (option?.fontSize) {
          this.pdfContentStyle.fontSize = option.fontSize;
        }

        // Enhanced settings for signature mode to match print quality
        if (option?.isUpload) {
          // Higher quality settings for documents that will be signed
          this.htmlToPdfOptions.html2canvas.dpi = 400; // Increased from 300
          this.htmlToPdfOptions.html2canvas.scale = 6; // Increased from 4
          this.htmlToPdfOptions.image.quality = 1.0; // Maximum quality
        } else {
          // Reset to default for regular print
          this.htmlToPdfOptions.html2canvas.dpi = 300;
          this.htmlToPdfOptions.html2canvas.scale = 4;
          this.htmlToPdfOptions.image.quality = 0.98;
        }

        const pdfContent = this.$refs.pdfContent;
        pdfContent.innerHTML = ""; // Clear previous content

        // Add page break classes
        if (header) {
          const headerElement = header.cloneNode(true);
          headerElement.classList.add("avoid-break");
          await pdfContent.appendChild(headerElement);
        }

        if (body) {
          const bodyElement = body.cloneNode(true);
          // Add page break classes to sections if needed
          const sections = bodyElement.querySelectorAll(".section");
          sections.forEach(section => {
            section.classList.add("avoid-break");
          });
          await pdfContent.appendChild(bodyElement);
        }

        if (footer) {
          const footerElement = footer.cloneNode(true);
          footerElement.classList.add("avoid-break");
          await pdfContent.appendChild(footerElement);
        }

        await this.$nextTick();
        // Ensure web fonts (Noto Sans Thai etc.) are fully loaded before
        // html2canvas rasterizes the DOM. Without this, the fallback font
        // is captured and Thai vowel/tone marks render misaligned.
        if (document.fonts && typeof document.fonts.ready?.then === "function") {
          try { await document.fonts.ready; } catch (_) { /* noop */ }
        }
        const data = await this.$refs.html2Pdf.generatePdf();
        return data;
      } catch (error) {
        console.error("PDF generation error:", error);
        this.isLoading = false;
        throw error;
      }
    },

    assignLodingTime(e) {
      this.loadingTime = e;
    },
    // Hook into vue-html2pdf BEFORE its own preview/download branches run.
    // Both preview-modal and enable-download are disabled, so the library
    // produces the paginated DOM but does not render its built-in iframe
    // (which gets blocked on some PCs). We generate the blob URL ourselves
    // and route by isUpload.
    async onBeforeDownload({ html2pdf, options, pdfContent }) {
      try {
        const blobUrl = await html2pdf()
          .set(options)
          .from(pdfContent)
          .output("bloburl");

        if (this.isUpload) {
          const res = await fetch(blobUrl);
          const pdfBlob = await res.blob();
          this.openSignDocTab(pdfBlob);
        } else {
          this.openPdfInNewTab(blobUrl);
        }
      } catch (error) {
        console.error("PDF generation error:", error);
        this.$bvToast.toast(
          "ไม่สามารถสร้างเอกสาร PDF ได้ กรุณาลองใหม่อีกครั้ง",
          {
            title: "เกิดข้อผิดพลาด",
            variant: "danger",
            autoHideDelay: 4000,
            solid: true,
          }
        );
      } finally {
        this.resetLodingTime();
      }
    },
    openPdfInNewTab(blobUrl) {
      // NOTE: do NOT pass "noopener" — with noopener, window.open returns null
      // even on success, which would always trigger the popup-blocked fallback.
      const win = window.open(blobUrl, "_blank");
      if (!win || win.closed || typeof win.closed === "undefined") {
        // Popup actually blocked — surface a dialog so user can open manually
        this.$emit("showSignDialog", {
          url: blobUrl,
          message: "กรุณาอนุญาต popup เพื่อเปิดเอกสาร PDF",
          title: "เปิดเอกสาร PDF",
        });
      }
    },
    openSignDocTab(pdfBlob) {
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const filename = `${
        this.remarkTitle
      }${this.$utils.generateDateTimeString()}_Signed`;

      const params = new URLSearchParams({
        src: pdfUrl,
        id: this.patientId,
        patientId: this.patientId,
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        filename: filename,
      }).toString();

      const signDocUrl = `/document?${params}`;

      this.openNewTabIpadFriendly(signDocUrl);
      this.$emit("openSignTabCompleted");
    },
    // Safety net: vue-html2pdf only fires this when previewModal or enableDownload
    // is true (both currently off), so this is dead code today. Kept to preserve
    // behavior if either prop is re-enabled.
    async hasDownloaded(pdfBlob) {
      this.resetLodingTime();
      if (!this.isUpload) return;
      this.openSignDocTab(pdfBlob);
    },
    resetLodingTime() {
      this.isLoading = false;
      setTimeout(() => {
        this.loadingTime = 0;
      }, 1000);
    },
    
    openNewTabIpadFriendly(url) {
      // Detect if we're on iOS/iPad
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      if (isIOS) {
        // For iOS: Try to open immediately, but with fallback if blocked
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        
        if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
          // Popup was blocked, show user-friendly dialog to manually open
          this.showIpadSignDialog(url);
        }
      } else {
        // For non-iOS devices, use regular window.open
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    
    showIpadSignDialog(url) {
      // Emit event to parent component to show a proper modal dialog
      this.$emit('showSignDialog', {
        url: url,
        message: 'เอกสารพร้อมสำหรับเซ็นต์แล้ว',
        title: 'เปิดหน้าเซ็นต์เอกสาร'
      });
    },
    async handleCreateMediaFile(pdfBlob) {
      await this.createMediaFiles({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        data: {
          category: "Document",
          patientId: this.patientId ? String(this.patientId) : null,
          remark: `${
            this.remarkTitle
          }${this.$utils.generateDateTimeString()}_Signed`,
          filesMetadata: [{ contentType: "application/pdf", fileSize: pdfBlob.size }],
        },
        files: [pdfBlob],
      });
    },
  },
  mounted() {
    // Apply styles to container
    const containerEl = document.querySelector(
      ".vue-html2pdf.my-export .layout-container .content-wrapper"
    );
    if (containerEl) {
      containerEl.style.setProperty("margin", "0", "important");
      containerEl.style.setProperty("background-color", "white", "important");
    }

    // Add global styles for PDF generation
    const styleEl = document.createElement("style");
    styleEl.textContent = `
      .avoid-break {
        page-break-inside: avoid !important;
        -webkit-page-break-inside: avoid !important;
      }
      .page-break-before {
        page-break-before: always !important;
        -webkit-page-break-before: always !important;
      }
      #my-pdf,
      #my-pdf table,
      #my-pdf th,
      #my-pdf td {
        font-family: 'Noto Sans Thai', 'Sarabun', sans-serif;
      }
      #my-pdf section {
        margin-bottom: 20px;
      }
    `;
    document.head.appendChild(styleEl);
  },

  beforeDestroy() {
    eventBus.$off("generatePDF");
  },
};
</script>

<style>
#my-pdf {
  font-size: 13px;
  margin: 0.5rem 3rem;
}

/* Additional styles for better page breaks */
.avoid-break {
  page-break-inside: avoid;
  -webkit-page-break-inside: avoid;
}

.page-break-before {
  page-break-before: always;
  -webkit-page-break-before: always;
}
</style>
